/* eslint-disable consistent-return */
import React from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import NoResultsFound from 'src/components/ui/NoResultsFound/NoResultsFound';
import WarningBox, { WarningBoxColorType } from 'src/components/ui/WarningBox/WarningBox';
import styles from './LinkedCasesTable.module.scss';
import { Case } from '../../../../services/case';
import Icon from '../../../ui/Icon/Icon';

export interface LinkedCaseTableProps {
  linkedCaseData: Case[];
  onLinkedCaseClosed?: (caseId: number) => void;
  onCaseClick?: (caseId: number) => void;
  allowClose?: boolean;
}
const LinkedCaseTable: React.FC<LinkedCaseTableProps> = ({
  linkedCaseData,
  onLinkedCaseClosed,
  onCaseClick,
  allowClose = true,
}) => {
  const { t } = useTranslation();
  const [cases, setCases] = React.useState(linkedCaseData || []);

  React.useEffect(() => {
    setCases(linkedCaseData || []);
  }, [linkedCaseData]);

  const closeCase = (id: number) => {
    if (!allowClose) return;
    if (onLinkedCaseClosed) {
      onLinkedCaseClosed(id);
      // eslint-disable-next-line array-callback-return
      const newCases = cases?.filter(c => {
        if (c && c.id) {
          return +c.id !== id;
        }
      });
      setCases(newCases);
    }
  };

  const handleRowClick = (event: React.MouseEvent, caseId: number) => {
    const target = event.target as HTMLElement;

    if (target.closest('button') || target.closest('input')) return;

    if (onCaseClick) {
      onCaseClick(caseId);
    }
  };

  return (
    <>
      {(!cases || cases.length === 0) && <NoResultsFound smallTitle title={t('outbound.no_linked_cases_found')} />}
      {cases && cases.length > 0 && !allowClose && (
        <WarningBox
          colorType={WarningBoxColorType.Warning}
          icon="warning"
          title={t('general.warning')}
          copy={t('case.no_close_permission')}
          className="mb-16"
        />
      )}
      {cases && cases.length > 0 && (
        <div className={classNames(styles.root, 'mb-16')}>
          <table>
            <thead>
              <tr>
                <th className="typo-text-12 text-faded">{t('general.term')}</th>
                <th className="typo-text-12 text-faded">{t('general.action')}</th>
              </tr>
            </thead>
            <tbody>
              {cases &&
                cases.map(c => {
                  return (
                    <tr
                      key={Math.random()}
                      className={`${styles.linked_cases} cursor-pointer hover:surface-elevated-more`}
                      onClick={e => handleRowClick(e, c?.id as number)}>
                      <td className="flex items-center gap-x-8 md:gap-x-12">
                        {c && (
                          <div>
                            <h4 className="typo-text-14--bold text-full">{c.topic}</h4>
                          </div>
                        )}
                      </td>
                      {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
                      <td>
                        <span
                          className={classNames({ 'opacity-40 pointer-events-none': !allowClose })}
                          title={allowClose ? undefined : t('case.no_close_permission')}>
                          <Icon
                            name="checkCircle"
                            width={24}
                            height={24}
                            onClick={(e: React.MouseEvent) => {
                              e.stopPropagation();
                              closeCase(Number(c.id));
                            }}
                          />
                        </span>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};

export default LinkedCaseTable;
